import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { saveTranscript } from "../../../shared/api/transcriptsApi";
import {
  SaveTranscriptRequest,
  UpdateTranscriptResponse,
} from "../../../shared/interfaces/Transcript";

interface UpdateError {
  status?: number;
  message: string;
}

interface UseUpdateTranscriptResult {
  mutate: (
    evidenceId: string,
    body: SaveTranscriptRequest,
  ) => Promise<UpdateTranscriptResponse>;
  isLoading: boolean;
  error: UpdateError | null;
  reset: () => void;
}

/**
 * Saves the transcript document through useMutation. Deliberately pessimistic
 * with NO cache writes or invalidations (as the old hand-rolled hook was):
 * the editor re-baselines its own draft store from the response and calls
 * refetchVersions() itself, and auto-invalidating the transcript-detail query
 * here would trigger a background refetch whose landing re-initializes the
 * draft store — clobbering any edits made since the save.
 *
 * Rejects with a plain `UpdateError` ({ status, message }) rather than the
 * axios error, because the save flow branches on `err.status === 403` and
 * renders `err.message` verbatim.
 */
export const useUpdateTranscript = (): UseUpdateTranscriptResult => {
  const mutation = useMutation<
    UpdateTranscriptResponse,
    UpdateError,
    { evidenceId: string; body: SaveTranscriptRequest }
  >({
    mutationFn: async ({ evidenceId, body }) => {
      try {
        return await saveTranscript(evidenceId, body);
      } catch (err) {
        const axiosErr = err as AxiosError<{ message?: string }>;
        const updateErr: UpdateError = {
          status: axiosErr.response?.status,
          message:
            axiosErr.response?.data?.message ?? "Failed to save transcript",
        };
        throw updateErr;
      }
    },
  });

  const { mutateAsync, reset } = mutation;

  // Keep the old two-argument call signature; mutateAsync rejects with the
  // mapped UpdateError exactly as the old hook's `throw updateErr` did.
  const mutate = useCallback(
    (evidenceId: string, body: SaveTranscriptRequest) =>
      mutateAsync({ evidenceId, body }),
    [mutateAsync],
  );

  return {
    mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset,
  };
};
